import { useQuery, useSubscription } from '@apollo/client';
import { isNil } from 'lodash';
import { useContext, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { Queries, Subscriptions } from '@shared/api';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBUserImage } from '@shared/components/image';
import { MBModalFromBottom } from '@shared/components/modal';
import { MBTooltip } from '@shared/components/tooltip';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2, MBTextCaption1 } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { CardRenderContext } from '../contexts/render';

interface Props {
  threadId: string;
}

const styles = StyleSheet.create({
  image: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'white',
    overflow: 'hidden',
  },
  tooltipPosition: {
    position: 'absolute',
    bottom: '100%',
    zIndex: 1,
  },
  tooltipContent: {
    width: 200,
  },
  modalContent: {
    maxHeight: '50vh',
  },
  userImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
});

const MAX_NUM_USERS_TO_SHOW = 16;

function useData(threadId: string) {
  return useQuery<API.CardChatThreadQuery, API.CardChatThreadQueryVariables>(
    Queries.card.chatThread,
    { variables: { id: threadId }, fetchPolicy: 'cache-and-network' },
  );
}

function useUsers(
  memberships: NonNullable<NonNullable<API.CardChatThreadQuery['thread']>['memberships']>,
): API.ShortUserFragment[] {
  return useMemo(() => memberships?.map(m => m.user) ?? [], [memberships]);
}

function usePreviewUsers(users: API.ShortUserFragment[]): API.ShortUserFragment[] {
  return useMemo(() => users?.slice(0, MAX_NUM_USERS_TO_SHOW) ?? [], [users]);
}

interface HoverContentProps {
  users: API.ShortUserFragment[];
}

function MBSeenByHoverContent({ users }: HoverContentProps) {
  const previewUsers = usePreviewUsers(users);
  const numUsersNotPreviewed = users.length - previewUsers.length;
  return (
    <MBView style={styles.tooltipPosition}>
      <MBTooltip theme="purple">
        <MBView fill style={styles.tooltipContent}>
          <MBTextBody2 color="white">{previewUsers.map(u => u.fullName).join('\n')}</MBTextBody2>
          {!!numUsersNotPreviewed && (
            <MBTextBody2 color="white">
              {i18n('card.blocks.seenBy.xMore', { count: numUsersNotPreviewed })}
            </MBTextBody2>
          )}
        </MBView>
      </MBTooltip>
    </MBView>
  );
}

export function MBSeenByBlock({ threadId }: Props) {
  const { data } = useData(threadId);
  // @ts-expect-error -- strict migration
  const users = useUsers(data?.thread?.memberships);
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const previewUsers = usePreviewUsers(users);
  const [modalVisible, showModal, hideModal] = useBoolean(false);

  useSubscription<API.ThreadSeenBySubscription, API.ThreadSeenBySubscriptionVariables>(
    Subscriptions.thread.seenBy,
    { variables: { input: { id: threadId } }, skip: isNil(threadId) },
  );

  if (!users) {
    return null;
  }
  return (
    <>
      <MBHover
        paddingVertical={1}
        paddingLeft={renderContext === 'taskList' ? 1 : 3}
        paddingRight={3}
        hoverContent={!!users.length && <MBSeenByHoverContent users={users} />}
      >
        <MBTouchableOpacity disabled={!users.length} onPress={showModal}>
          <View row centerVertically>
            <MBTextCaption1 color={colors.foregroundInactive}>
              {i18n('card.blocks.seenBy.text', { count: users.length })}
            </MBTextCaption1>
            <View paddingLeft={2} />

            {previewUsers.map(user => (
              <MBUserImage key={user.id} user={user} size="tiny" style={styles.image} />
            ))}
          </View>
        </MBTouchableOpacity>
      </MBHover>
      {/* TODO(fant): move this out into a separate file/component */}
      <MBModalFromBottom
        visible={modalVisible}
        onClose={hideModal}
        title={i18n('card.blocks.seenBy.modalTitle')}
      >
        <ScrollView style={styles.modalContent}>
          {users.map(user => (
            <View row paddingVertical={1} key={user.id}>
              <MBUserImage size="small" user={user} style={styles.userImage} />
              <View paddingRight={1} />
              <MBTextBody2>{user.fullName}</MBTextBody2>
            </View>
          ))}
        </ScrollView>
      </MBModalFromBottom>
    </>
  );
}
